import React, { SyntheticEvent, useCallback } from 'react'

import styled from '@emotion/styled'
import {
  Box,
  Display,
  DisplayPosition,
  FlexDirection,
  JustifyContent,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { CookingTimeText } from '../CookingTimeText'
import { RecipeTilePurchaseInfo } from '../RecipeTilePurchaseInfo'
import { TileImage } from '../TileImage/TileImage'
import { Title } from '../Title'
import {
  cssAddRecipeButtonContainer,
  cssRecipeTagHolder,
  cssRecipeTile,
  cssRecipeTileContainer,
  cssRecipeTileIsFineDineIn,
} from './styles'
import { AddRecipeButton } from '../AddRecipeButton'
import { DietaryLabels } from '../DietaryLabels'
import { LabelWrapper } from '../Label/LabelWrapper'
import { SoldOutOverlay } from '../SoldOutOverlay'
import { SwapAlternativeOptions } from '../SwapAlternativeOptions/SwapAlternativeOptions'
import { getOverlayColor } from '../../utils/getOverlayColor'
import { CaloriesText } from '../Calories'
import {
  CollectionUUID,
  isRecipeFineDineIn,
  MenuApiPreferencesResponse,
  Recipe,
} from '@library/menu-service'
import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'
import { ProteinText } from '../Protein'

const OuterContainer = styled.div(cssRecipeTile)

const TileContainer = styled.div((props: any) => ({
  ...cssRecipeTileContainer,
  ...(props.isFineDineIn ? cssRecipeTileIsFineDineIn : {}),
}))

const RecipeTagContainer = styled.span(() => ({
  ...cssRecipeTagHolder,
}))

const AddRecipeButtonContainer = styled.span((props: any) => ({
  ...cssAddRecipeButtonContainer,
  ...(props.isFineDineIn ? cssRecipeTileIsFineDineIn : {}),
}))

const ImageContainer = withCitrusTheme(Box, () => ({
  minHeight: '200px',
  maxHeight: '270px',
  position: 'relative',
  overflow: 'hidden',
}))

export type RecipeTileProps = {
  recipe: Recipe
  currentCollectionId: CollectionUUID
  onClick: (recipeId: string) => void
  menuPreferences?: MenuApiPreferencesResponse
  setBrowserCTAVisible: () => void
  canAddRecipes: boolean
  addRecipe: (recipeId: string) => void
  removeRecipe: (recipeId: string) => void
  reachedLimit: boolean
  isInBasket: boolean
  isOutOfStock: boolean
  selectVariant: ({
    variant,
    variantOutOfStock,
    originalRecipeId,
    recipeReference,
  }: {
    variant: Recipe
    variantOutOfStock: boolean
    originalRecipeId: string
    recipeReference: string
  }) => void
  alternativeOptions: AlternativeOptions
  recipeReference: string
  originalId: string
  isProteinOnSignupEnabled?: boolean
  lazy?: boolean
  insideOverflow?: boolean
}

export const RecipeTile = ({
  recipe,
  currentCollectionId: categoryId,
  onClick,
  menuPreferences,
  setBrowserCTAVisible,
  canAddRecipes,
  addRecipe,
  removeRecipe,
  reachedLimit,
  isInBasket,
  isOutOfStock,
  selectVariant,
  alternativeOptions,
  recipeReference,
  originalId,
  isProteinOnSignupEnabled,
  lazy = true,
  insideOverflow = false,
}: RecipeTileProps) => {
  const { coreId: recipeCoreId, id: recipeUuid } = recipe
  const isFineDineIn = isRecipeFineDineIn(recipe)

  const selectRecipeVariant = useCallback(
    ({ variant, variantOutOfStock }: { variant: Recipe; variantOutOfStock: boolean }) => {
      selectVariant({
        variant,
        variantOutOfStock,
        originalRecipeId: originalId,
        recipeReference,
      })
    },
    [originalId, recipeReference, selectVariant],
  )

  // should never happen but caters for loading state
  if (categoryId === null) {
    return null
  }

  // alternative options include the recipe itself
  const hasAlternativeOptions = alternativeOptions.length > 1

  const handleOnClick = (e: SyntheticEvent) => {
    e.stopPropagation()
    onClick(recipeUuid)
  }

  const handleOnClickRecipeButtonContainer = (e: SyntheticEvent) => {
    // Prevents opening recipe details modal when clicking on disabled add button
    if (isOutOfStock) e.stopPropagation()
  }

  return (
    <OuterContainer
      role="button"
      tabIndex={0}
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      data-testing-id={recipeCoreId}
      onClick={handleOnClick}
      onKeyPress={handleOnClick}
    >
      <TileContainer isFineDineIn={isFineDineIn}>
        <ImageContainer>
          <SoldOutOverlay isOutOfStock={isOutOfStock} />
          <TileImage lazy={lazy} recipe={recipe} insideOverflow={insideOverflow} />
          <RecipeTagContainer>
            <DietaryLabels recipe={recipe} />
          </RecipeTagContainer>
        </ImageContainer>

        <Box position={DisplayPosition.Relative} flexGrow={1} style={{ margin: '1rem' }}>
          <Box display={Display.Flex} paddingBottom={2}>
            <LabelWrapper isOutOfStock={isOutOfStock} recipe={recipe} />
            <AddRecipeButtonContainer
              isFineDineIn={isFineDineIn}
              isSoldOut={isOutOfStock}
              onClick={handleOnClickRecipeButtonContainer}
            >
              <AddRecipeButton
                recipeId={recipeCoreId}
                setBrowserCTAVisible={setBrowserCTAVisible}
                canAddRecipes={canAddRecipes}
                addRecipe={addRecipe}
                removeRecipe={removeRecipe}
                reachedLimit={reachedLimit}
                isInBasket={isInBasket}
                isOutOfStock={isOutOfStock}
              />
            </AddRecipeButtonContainer>
          </Box>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            justifyContent={JustifyContent.SpaceBetween}
            height="100%"
            position={DisplayPosition.Relative}
          >
            <SoldOutOverlay backgroundColor={getOverlayColor(recipe)} isOutOfStock={isOutOfStock} />
            <Box>
              <Title title={recipe.name} />
              <Space size={2}></Space>
              <CookingTimeText recipe={recipe} />
              {(!menuPreferences || menuPreferences?.display_calories) && (
                <CaloriesText recipe={recipe} />
              )}
              {isProteinOnSignupEnabled && <ProteinText recipe={recipe} />}
            </Box>
            <RecipeTilePurchaseInfo isOutOfStock={isOutOfStock} recipe={recipe} />
          </Box>
          {hasAlternativeOptions && (
            <Box position={DisplayPosition.Absolute} bottom="0" right="0" width="100%">
              <SwapAlternativeOptions
                currentRecipeCoreId={recipeCoreId}
                currentCollectionId={categoryId}
                recipe={recipe}
                selectVariant={selectRecipeVariant}
                alternativeOptions={alternativeOptions}
              />
            </Box>
          )}
        </Box>
      </TileContainer>
    </OuterContainer>
  )
}
